import { Option } from "@inrev/core";
import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { Text } from "../../primitives";

type MeasurePart = "separator" | "count" | "label";

/**
 * How many labels fit in `available` pixels: all of them when they fit without the "+N more"
 * count, otherwise as many as fit beside it. Never fewer than one: the first label truncates
 * rather than vanishing.
 */
export const countLabelsThatFit = ({
	labelWidths,
	separatorWidth,
	countWidth,
	countGap,
	available,
}: {
	labelWidths: readonly number[];
	separatorWidth: number;
	countWidth: number;
	/** Space between the shown labels and the "+N more" count. */
	countGap: number;
	available: number;
}) => {
	const ends = labelWidths.reduce<number[]>(
		(acc, width) => [
			...acc,
			Option.mapOrElse(acc.at(-1), (end) => end + separatorWidth + width, width),
		],
		[],
	);
	if ((ends.at(-1) ?? 0) <= available) return labelWidths.length;
	const fitBesideCount = ends.filter((end) => end + countGap + countWidth <= available);
	return Math.max(fitBesideCount.length, 1);
};

/**
 * Lists labels on one line, as many as the width allows, then "+N more" for the rest. Screen
 * readers get the full list, and the pointer gets it as a tooltip whenever some is cut off.
 */
export const FittedLabels = ({
	labels,
	className,
}: {
	labels: readonly string[];
	className?: string;
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const measureRef = useRef<HTMLSpanElement>(null);
	const [fit, setFit] = useState({ count: labels.length, clipped: false });
	// Keyed on the text, so a caller passing a new array of the same labels doesn't re-measure.
	const labelsKey = labels.join("\u0000");

	useLayoutEffect(() => {
		const container = containerRef.current;
		const measure = measureRef.current;
		if (!container || !measure) return;
		const measureFit = () => {
			const spansOf = (part: MeasurePart) => [
				...measure.querySelectorAll(`[data-measure="${part}"]`),
			];
			const labelSpans = spansOf("label");
			const available = container.clientWidth;
			// No width means no layout yet (hidden, or a test DOM), so there is nothing to measure.
			if (available === 0) {
				setFit({ count: labelSpans.length, clipped: false });
				return;
			}
			const widthOf = (element: Element | undefined) => element?.getBoundingClientRect().width ?? 0;
			const labelWidths = labelSpans.map(widthOf);
			setFit({
				count: countLabelsThatFit({
					labelWidths,
					separatorWidth: widthOf(spansOf("separator")[0]),
					countWidth: widthOf(spansOf("count")[0]),
					countGap: Number.parseFloat(getComputedStyle(container).columnGap) || 0,
					available,
				}),
				clipped: (labelWidths[0] ?? 0) > available,
			});
		};
		measureFit();
		// The measure span also resizes once web fonts land, even when the container doesn't.
		const observer = new ResizeObserver(measureFit);
		observer.observe(container);
		observer.observe(measure);
		return () => observer.disconnect();
	}, [labelsKey]);

	const shown = Math.min(fit.count, labels.length);
	const leftOut = labels.length - shown;

	return (
		<div
			ref={containerRef}
			title={leftOut > 0 || fit.clipped ? labels.join(", ") : undefined}
			className={cn("relative flex min-w-0 flex-1 items-center gap-1 overflow-hidden", className)}
			data-testid="fitted-labels"
		>
			<Text className="sr-only">{labels.join(", ")}</Text>
			<Text truncate aria-hidden className="min-w-0">
				{labels.slice(0, shown).join(", ")}
			</Text>
			{leftOut > 0 && (
				<Text aria-hidden color="secondary" className="shrink-0" data-testid="fitted-labels-more">
					{`+${leftOut} more`}
				</Text>
			)}
			<Text
				ref={measureRef}
				aria-hidden
				className="pointer-events-none invisible absolute left-0 top-0 whitespace-pre"
			>
				<span data-measure="separator">, </span>
				<span data-measure="count">{`+${labels.length} more`}</span>
				{labels.map((label, index) => (
					<span key={index} data-measure="label">
						{label}
					</span>
				))}
			</Text>
		</div>
	);
};
